import React from "react"
import EChartsReact from "@/components/EChartsReact"
import moment from "moment"

/**
 * EUI Rolling 12 Month Trend 基础图表组件
 * 提供最基础的ECharts功能，接收原始数据作为props
 * 
 * @param {Object} props
 * @param {Array} props.electricityData - 电力数据数组，格式：[{month: 'YYYY-MM', rolling_eui: number}]
 * @param {Array} props.gasData - 燃气数据数组，格式：[{month: 'YYYY-MM', rolling_eui: number}]
 * @param {string} props.title - 图表标题
 * @param {string} props.className - CSS类名
 * @param {Object} props.style - 样式对象
 * @param {Object} props.customOptions - 自定义ECharts配置选项（可选）
 */
export const EUIRolling12MonthTrendBaseChart = ({ 
  electricityData = [], 
  gasData = [], 
  title = 'EUI Rolling 12 Month Trend (kBtu/sqft-yr)',
  className = '',
  style = { width: '100%', height: '100%' },
  customOptions = {},
  aiContext
}) => {
  
  // 合并所有数据获取月份列表
  const allMonths = [...new Set([...electricityData, ...gasData].map(item => item.month))];
  
  // 基础ECharts配置
  const baseOption = {
    backgroundColor: '#fff',
    title: {
      text: title,
      left: 'center',
      top: '2%',
      textStyle: {
        color: '#396598',
        fontSize: 14
      }
    },
    legend: {
      data: ['Electricity', 'Gas'],
      left: 'center',
      top: '10%',
      itemWidth: 10,
      itemHeight: 10
    },
    xAxis: [
      {
        type: 'category',
        boundaryGap: true,
        data: allMonths,
        axisLabel: {
          rotate: 50,
          formatter: function(value) {
            return moment(value).format('MMM');
          }
        },
        position: 'bottom'
      },
      {
        type: 'category',
        position: 'top',
        boundaryGap: true,
        axisLine: {
          show: true
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          formatter: function(value) {
            return moment(value).format('YYYY');
          }
        },
        data: allMonths
      }
    ],
    yAxis: {
      type: 'value'
    },
    series: [
      {
        data: electricityData.map(item => parseFloat(item?.rolling_eui || 0).toFixed(2)),
        type: 'bar',
        name: 'Electricity',
        stack: 'total',
        itemStyle: {
          color: '#4472c4',
          borderRadius: [0, 0, 0, 0]
        },
        barGap: 0
      },
      {
        data: gasData.map(item => parseFloat(item?.rolling_eui || 0).toFixed(2)),
        name: 'Gas',
        type: 'bar',
        stack: 'total',
        itemStyle: {
          color: '#c00000',
          borderRadius: [6, 6, 0, 0]
        },
        barGap: 0
      }
    ],
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        const date = moment(params[0].axisValue).format('MMM YYYY');
        let result = `${date}<br/>`;
        
        let total = 0;
        params.forEach(param => {
          const value = param.value;
          const marker = param.marker;
          const seriesName = param.seriesName;
          const formattedValue = `${value} kBtu/sqft-yr`;
          total += parseFloat(value);
          
          result += `${marker}${seriesName}: ${formattedValue}<br/>`;
        });
        
        result += `Total: ${total.toFixed(2)} kBtu/sqft-yr`;
        
        return result;
      }
    },
    grid: {
      top: '20%',
      left: '6%',
      right: '6%',
      bottom: '4%',
      containLabel: true
    }
  };

  // 合并自定义配置
  const finalOption = { ...baseOption, ...customOptions };

  return (
    <EChartsReact 
      className={className} 
      option={finalOption} 
      style={style} 
      aiContext={aiContext}
    />
  );
};
